import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Box, Button, ButtonGroup, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import type React from "react";
import { useRef, useState } from "react";
import {
  reportOrderCardActionButtonBaseSx,
  reportOrderCardActionButtonSx,
} from "./reportOrderCardActionButtonSx";
import type { ReportCardFinanceAction } from "./reportCardFinanceActions";

interface ReportOrderCardFinanceActionsMenuProps {
  menuTestId: string;
  menuId: string;
  actions: ReportCardFinanceAction[];
  ariaLabel: string;
}

const renderMenuItem = (
  action: ReportCardFinanceAction,
  onSelect: (action: ReportCardFinanceAction) => void,
) => {
  if (!action.disabled || !action.disabledHint) {
    return (
      <MenuItem
        key={action.id}
        disabled={action.disabled}
        onClick={() => onSelect(action)}
        data-testid={action.testId}
      >
        <ListItemText>{action.label}</ListItemText>
      </MenuItem>
    );
  }

  return (
    <Tooltip key={action.id} title={action.disabledHint} arrow placement="left">
      <Box component="span" sx={{ display: "block" }}>
        <MenuItem disabled data-testid={action.testId}>
          <ListItemText>{action.label}</ListItemText>
        </MenuItem>
      </Box>
    </Tooltip>
  );
};

const ReportOrderCardFinanceActionsMenu: React.FC<ReportOrderCardFinanceActionsMenuProps> = ({
  menuTestId,
  menuId,
  actions,
  ariaLabel,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const visibleActions = actions.filter((action) => !action.hidden);
  const primaryAction = visibleActions.find((action) => !action.disabled) ?? null;
  const menuActions = primaryAction
    ? visibleActions.filter((action) => action.id !== primaryAction.id)
    : visibleActions;

  const closeMenu = () => setMenuOpen(false);

  const handleAction = (action: ReportCardFinanceAction) => {
    if (action.disabled) {
      return;
    }
    closeMenu();
    action.onClick();
  };

  const menuTriggerButton = (
    <Button
      size="small"
      variant="outlined"
      aria-label={ariaLabel}
      aria-controls={menuOpen ? menuId : undefined}
      aria-expanded={menuOpen ? "true" : undefined}
      aria-haspopup="menu"
      onClick={() => setMenuOpen((open) => !open)}
      data-testid={menuTestId}
      sx={{
        ...reportOrderCardActionButtonSx,
        minWidth: 0,
        px: 0.75,
      }}
    >
      <ArrowDropDownIcon fontSize="small" />
    </Button>
  );

  return (
    <>
      <Box ref={anchorRef}>
        {primaryAction ? (
          <ButtonGroup
            variant="outlined"
            disableElevation
            sx={{
              "& .MuiButtonGroup-grouped": {
                ...reportOrderCardActionButtonBaseSx,
                borderRadius: 0,
                margin: 0,
                minWidth: 0,
                "&:not(:last-of-type)": {
                  borderRight: 0,
                },
              },
              "& .MuiButtonGroup-grouped:first-of-type": {
                borderTopLeftRadius: 1.5,
                borderBottomLeftRadius: 1.5,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiButtonGroup-grouped:last-of-type": {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: 1.5,
                borderBottomRightRadius: 1.5,
                px: 0.75,
                minWidth: 0,
              },
            }}
          >
            <Button size="small" onClick={primaryAction.onClick} data-testid={primaryAction.testId}>
              {primaryAction.label}
            </Button>
            <Button
              size="small"
              aria-controls={menuOpen ? menuId : undefined}
              aria-expanded={menuOpen ? "true" : undefined}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((open) => !open)}
              data-testid={menuTestId}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        ) : (
          menuTriggerButton
        )}
      </Box>
      <Menu
        id={menuId}
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuActions.map((action) => renderMenuItem(action, handleAction))}
      </Menu>
    </>
  );
};

export default ReportOrderCardFinanceActionsMenu;
